/**
 * Plan a campaign: the production planner's steps, built for speed. One prepare call when the page opens; what has to
 * be written (insights, concepts, a market brief, the creator history) starts the moment the page opens and again when
 * the setup changes, so it is ready by the step that shows it. The media plan is built once, then only rebuilt when the
 * client asks; every edit is priced at once. Ordering writes the client order and creates the campaign.
 *
 * Lykke sits on the left (in the new layout, as a mode of her panel): what the client writes, says or sends fills the
 * steps, and she takes them to the step that shows it.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check, ChevronDown, Loader2, MessageCircle } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { useEffectiveBrand } from '@/cowork/useActiveBrand';
import { StepsSlot } from '@/cowork/campaignOrder/v5/styles';
import { AgentPanelSlot, useInShell } from '@/cowork/shell/ShellContext';
import { plannerApi, type Evaluated, type Prepared } from './api';
import { conceptForm, initialState, lykkePatch, lykkeView, migrateDraft, planKeyOf, PLAN_KEYS, shapedPatch, shapeKey, STEPS, toValues, fmt, short, type PlannerState, type Step } from './state';
import { PlannerLykke, clearPlannerChat } from './PlannerLykke';
import { GoalStep, InsightsStep, SetupStep } from './StepsA';
import { AudienceStep, CreatorsStep } from './StepsC';
import { CheckoutStep, ConceptStep, DetailsStep, DoneStep, MediaStep, type Ahead } from './StepsB';

export function PlannerPage() {
  const { brandId } = useEffectiveBrand();
  const prepQ = useQuery({ queryKey: ['planner', 'prepare', brandId], queryFn: () => plannerApi.prepare(brandId!), enabled: !!brandId, staleTime: 5 * 60_000, retry: false });
  if (!brandId) return <Centered>Pick a brand in the top bar to plan a campaign.</Centered>;
  if (prepQ.isLoading) return <Centered><Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />Opening your Brand OS</Centered>;
  if (prepQ.error || !prepQ.data) return <Centered>{(prepQ.error as Error)?.message || 'Planning is not available right now.'}</Centered>;
  return <Planner key={brandId} p={prepQ.data} />;
}
const Centered = ({ children }: { children: React.ReactNode }) => <div className="grid min-h-[60vh] place-items-center px-4 text-center text-sm text-[#667085]"><p className="max-w-md">{children}</p></div>;

const DRAFT_KEY = (brandId: string) => `planner-draft-v3:${brandId}`;
function loadDraft(brandId: string): { s: PlannerState; step: Step; max: Step } | null {
  const read = (k: string) => { try { const j = JSON.parse(localStorage.getItem(k) || 'null'); return j && Date.now() - j.at < 7 * 86400_000 ? j : null; } catch { return null; } };
  const v3 = read(DRAFT_KEY(brandId)); if (v3) return v3;
  // From before strategies: their free strategy text becomes their idea. Read once, so an order never brings it back.
  const v2 = read(`planner-draft-v2:${brandId}`);
  try { localStorage.removeItem(`planner-draft-v2:${brandId}`); } catch { /* private mode */ }
  return v2 ? migrateDraft(v2) : null;
}

function Planner({ p }: { p: Prepared }) {
  const brandId = p.brand.id;
  const draft = useMemo(() => loadDraft(brandId), [brandId]);
  const [s, setS] = useState<PlannerState>(() => (draft ? { ...initialState(p), ...draft.s, agree: false, authority: false } : initialState(p)));
  const [step, setStep] = useState<Step>(draft?.step ?? 0);
  const [maxStep, setMaxStep] = useState<Step>(draft?.max ?? 0);
  const set = useCallback((patch: Partial<PlannerState>) => setS((x) => ({ ...x, ...patch })), []);
  useEffect(() => { try { localStorage.setItem(DRAFT_KEY(brandId), JSON.stringify({ at: Date.now(), s, step, max: maxStep })); } catch { /* private mode */ } }, [brandId, s, step, maxStep]);

  const objective = s.objective || (s.type === 'ugc' ? 'Content production (UGC)' : 'Awareness');

  // ── Warm ahead: from the moment the page opens, and again when what the writing depends on changes ──
  const warmKey = `${s.market}|${objective}|${s.product}`;
  useEffect(() => {
    if (!s.product) return;
    const t = setTimeout(() => { void plannerApi.warm({ brandId, market: s.market, objective, product: s.product, audience: s.targetAudience.slice(0, 300) }).catch(() => undefined); }, 300);
    return () => clearTimeout(t);
  }, [warmKey]);

  const insightsQ = useQuery({
    queryKey: ['planner', 'insights', brandId, s.market], queryFn: () => plannerApi.insights(brandId, s.market),
    initialData: s.market === p.defaults.market && p.insights ? p.insights : undefined, refetchInterval: (q) => (q.state.data?.v === 2 || q.state.dataUpdateCount > 40 ? false : 3000),
  });
  const qc = useQueryClient();
  const insightsWriting = insightsQ.data?.v !== 2 && (qc.getQueryState(['planner', 'insights', brandId, s.market])?.dataUpdateCount ?? 0) <= 40;
  const conceptsQ = useQuery({
    queryKey: ['planner', 'concepts', brandId, warmKey], queryFn: () => plannerApi.concepts({ brandId, market: s.market, objective, product: s.product }),
    enabled: !!s.product, refetchInterval: (q) => (q.state.data?.status === 'ready' || q.state.dataUpdateCount > 40 ? false : 3000),
  });

  // ── The media plan: built once from the client's choices, then only rebuilt when they ask ──
  const [rebuilding, setRebuilding] = useState(false);
  const suggest = useCallback(async () => {
    setRebuilding(true);
    try {
      const r = await plannerApi.suggest({ type: s.type, objective, budget: s.budget, currency: s.currency, platforms: s.platforms, band: s.band, ageMin: Number(s.audienceAgeMin) || undefined, ageMax: Number(s.audienceAgeMax) || undefined, paidUsage: s.paidUsage });
      setS((x) => ({ ...x, blocks: r.blocks, blocksEdited: false, planBudget: x.budget, planKey: planKeyOf(x) }));
    } catch { /* keep the blocks */ } finally { setRebuilding(false); }
  }, [s.type, objective, s.budget, s.currency, s.platforms, s.band, s.audienceAgeMin, s.audienceAgeMax, s.paidUsage]);
  useEffect(() => {
    if (s.blocks.length || maxStep < 3 || rebuilding) return; // the first plan is built while the client is on the creators step
    void suggest();
  }, [maxStep >= 3, s.blocks.length]);
  const stale = !!s.planKey && s.planKey !== planKeyOf(s);

  const values = useMemo(() => toValues(s, p), [s, p]);
  const [ev, setEv] = useState<Evaluated | null>(null);
  const [evLoading, setEvLoading] = useState(false);
  const evKey = JSON.stringify([values, s.blocks]);
  useEffect(() => {
    if (!s.blocks.length) return;
    setEvLoading(true); let live = true;
    const t = setTimeout(() => { plannerApi.evaluate(values, s.blocks).then((r) => { if (live) setEv(r); }).catch(() => undefined).finally(() => { if (live) setEvLoading(false); }); }, 120);
    return () => { live = false; clearTimeout(t); };
  }, [evKey]);

  // ── Their idea from Insights, shaped ahead while they pick creators (one call in flight; nothing moves under them) ──
  const [ahead, setAhead] = useState<Ahead['state']>('idle');
  const aheadRef = useRef<string | null>(null);
  const stepRef = useRef(step); stepRef.current = step;
  const sRef = useRef(s); sRef.current = s;
  const shapeAhead = useCallback(() => {
    const x = sRef.current; const key = shapeKey(x);
    if (aheadRef.current === key) return;
    aheadRef.current = key; setAhead('pending');
    plannerApi.concept(conceptForm(x, p, { text: x.ideaText.trim(), fresh: true })).then(({ concept }) => {
      setS((y) => {
        const empty = !y.summary && !y.conceptTitle; const stillAhead = y.builtOn?.from === 'ahead' && stepRef.current !== 5;
        if (shapeKey(y) !== key || !(empty || stillAhead)) return { ...y, shapedFrom: key };
        return { ...y, ...shapedPatch(y, concept, { from: 'ahead', text: y.ideaText, insights: insightsQ.data || null }), shapedFrom: key };
      });
      setAhead('idle');
    }).catch(() => setAhead('failed')).finally(() => { if (aheadRef.current === key) aheadRef.current = null; });
  }, [p, insightsQ.data]);
  const aheadKey = shapeKey(s);
  useEffect(() => {
    if (step === 2 || s.ideaText.trim().length < 10 || s.shapedFrom === aheadKey) return;
    if ((s.summary || s.conceptTitle) && s.builtOn?.from !== 'ahead') return; // their own concept on Concept wins
    shapeAhead();
  }, [step, aheadKey, s.shapedFrom]);

  // ── Steps ──
  const canLeave = (st: Step) => (st === 0 ? !!s.product.trim() : st === 1 ? !!s.objective : true);
  const goTo = (st: Step) => {
    if (st > maxStep + 1 || (st > maxStep && !canLeave(maxStep))) return;
    setStep(st); if (st > maxStep) setMaxStep(st);
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // The planner scrolls inside its own area, so the footer (total, Back, Next) always stays on screen.
  const frameRef = useRef<HTMLDivElement>(null); const mainRef = useRef<HTMLElement>(null);
  const [frameH, setFrameH] = useState<number | null>(null);
  useLayoutEffect(() => {
    const fit = () => { const top = frameRef.current?.getBoundingClientRect().top ?? 0; setFrameH(Math.max(400, window.innerHeight - Math.max(0, top))); };
    fit(); window.addEventListener('resize', fit); return () => window.removeEventListener('resize', fit);
  }, []);

  // ── Lykke on the left: her changes go into the state; she moves the client once they have landed ──
  const [lykkeGo, setLykkeGo] = useState<Step | null>(null);
  const [rebuildSoon, setRebuildSoon] = useState(false);
  const evRef = useRef(ev); evRef.current = ev;
  const missing = (x: PlannerState) => [!x.product.trim() && 'What you promote', !x.objective && 'Goal', ...(evRef.current?.gaps || []).map((g) => g.label)].filter(Boolean) as string[];
  const lykkeContext = () => ({ state: lykkeView(sRef.current, p, insightsQ.data || null), step: stepRef.current, missing: missing(sRef.current) });
  const onLykke = (patch: Record<string, unknown>) => {
    setS((x) => ({ ...x, ...lykkePatch(patch, x, p, insightsQ.data || null) }));
    if (Object.keys(patch).some((k) => PLAN_KEYS.includes(k))) setRebuildSoon(true);
  };
  useEffect(() => {
    if (lykkeGo == null) return;
    // As far as the steps before it allow (a step needs what came before it), then on screen.
    let to = lykkeGo; for (let i = 0; i < lykkeGo; i++) if (!canLeave(i as Step)) { to = i as Step; break; }
    setStep(to); setMaxStep((m) => (to > m ? to : m)); setLykkeGo(null);
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [lykkeGo, s.product, s.objective]);
  useEffect(() => {
    if (!rebuildSoon) return;
    setRebuildSoon(false);
    if (s.blocks.length && !s.blocksEdited && s.planKey !== planKeyOf(s)) void suggest();
  }, [rebuildSoon, suggest]);
  const opener = !s.product.trim() ? 'What are you promoting?' : !s.objective ? `What should the ${s.product} campaign achieve?` : step <= 2 && !s.strategy ? 'Which strategy on Insights fits, or what is your own idea?' : missing(s)[0] ? `Still needed: ${missing(s)[0]}. What should it be?` : 'Tell me what to change, or check and order.';
  const [chatOpen, setChatOpen] = useState(false);
  const inShell = useInShell();
  const lykke = (inPanel: boolean, className?: string) => <PlannerLykke brandId={brandId} context={lykkeContext} opener={opener} onAnswer={onLykke} onGo={setLykkeGo} inPanel={inPanel} className={className} />;

  // ── Order ──
  const [ordering, setOrdering] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; name: string; note: string | null } | null>(null);
  const placing = useRef(false);
  const order = async () => {
    if (placing.current) return; placing.current = true; setOrdering(true); setOrderError(null);
    try {
      const r = await plannerApi.order({ brandId, values, blocks: s.blocks, agree: s.agree, authority: s.authority });
      setDone({ id: r.order.id, name: r.order.campaignName || String(values.campaign_name || ''), note: r.note });
      try { localStorage.removeItem(DRAFT_KEY(brandId)); } catch { /* ignore */ }
      clearPlannerChat(brandId);
    } catch (e) { setOrderError((e as Error).message); } finally { setOrdering(false); placing.current = false; }
  };

  if (done) return <main className="min-h-[calc(100vh-3.5rem)] bg-[#f8f9fa] px-4 sm:px-8"><DoneStep name={done.name} note={done.note} orderId={done.id} /></main>;

  const nav = (
    <nav aria-label="Steps" className="flex flex-wrap items-center justify-end gap-0.5">
      {STEPS.map((label, i) => {
        const st = i as Step; const current = st === step; const reachable = st <= maxStep || (st === maxStep + 1 && canLeave(maxStep)); const ok = st < maxStep && !current;
        return (
          <button key={label} type="button" disabled={!reachable} onClick={() => goTo(st)} aria-current={current ? 'step' : undefined}
            className={cn('inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[12px] transition-colors', current ? 'text-[#101828]' : 'text-[#667085] hover:text-[#101828]', !reachable && 'opacity-40')} style={{ fontWeight: current ? 600 : 500 }}>
            <span className={cn('grid h-5 w-5 place-items-center rounded-full text-[10.5px]', current ? 'bg-[#2e024c] text-white' : ok ? 'bg-[#be43ff]/10 text-[#be43ff]' : 'border border-[#d0d5dd] text-[#667085]')} style={{ fontWeight: 600 }}>{ok ? <Check className="h-3 w-3" aria-hidden /> : i + 1}</span>
            <span className="hidden lg:inline">{label}</span>
          </button>
        );
      })}
    </nav>
  );
  const props = { p, s, set };
  return (
    <div ref={frameRef} className="flex flex-col overflow-hidden bg-[#f8f9fa] lg:flex-row" style={{ height: frameH ?? 'calc(100vh - 3.5rem)' }}>
      {inShell ? (
        <AgentPanelSlot id="planner" label="Plan" line="Planning your campaign with you">{lykke(true)}</AgentPanelSlot>
      ) : (
        <>
          {/* Lykke: a column on desktop, a panel you open on the phone. */}
          <button type="button" onClick={() => setChatOpen((v) => !v)} className="flex flex-none items-center gap-2 border-b border-gray-100 bg-white px-4 py-3 text-left lg:hidden" aria-expanded={chatOpen}>
            <MessageCircle className="h-4 w-4 text-[#be43ff]" aria-hidden /><span className="flex-1 text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>Lykke, your account manager</span>
            <ChevronDown className={cn('h-4 w-4 text-[#98a2b3] transition', chatOpen && 'rotate-180')} aria-hidden />
          </button>
          {lykke(false, cn('border-gray-100 lg:flex lg:h-full lg:w-[380px] lg:flex-none lg:border-r', chatOpen ? 'flex h-[60vh] flex-none border-b' : 'hidden'))}
        </>
      )}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto px-4 pb-10 pt-6 sm:px-8 lg:pt-8">
        <div className="mx-auto flex max-w-[1080px] flex-col gap-6">
          <div className="-mb-2">{nav}</div>
          <StepsSlot.Provider value={null}>
            {step === 0 && <SetupStep {...props} />}
            {step === 1 && <GoalStep {...props} />}
            {step === 2 && <InsightsStep {...props} insights={insightsQ.data || null} writing={insightsWriting} />}
            {step === 3 && <CreatorsStep {...props} />}
            {step === 4 && <AudienceStep {...props} />}
            {step === 5 && <ConceptStep {...props} concepts={conceptsQ.data?.concepts || []} insights={insightsQ.data || null} onStep={goTo} ahead={{ state: ahead, retry: () => { aheadRef.current = null; shapeAhead(); } }} />}
            {step === 6 && <MediaStep {...props} ev={ev} evLoading={evLoading} onRebuild={() => { void suggest(); }} rebuilding={rebuilding} stale={stale} />}
            {step === 7 && <DetailsStep {...props} ev={ev} />}
            {step === 8 && <CheckoutStep {...props} ev={ev} onStep={goTo} onOrder={() => { void order(); }} ordering={ordering} orderError={orderError} />}
          </StepsSlot.Provider>
        </div>
      </main>
      <footer className="z-10 flex flex-none items-center gap-3 border-t border-[#eaecf0] bg-white py-3 pl-4 pr-4 sm:pl-8 sm:pr-40">
        <div className="min-w-0 flex-1 text-[12px] text-[#667085]">
          {ev && ev.total > 0 ? <><b className="text-[15px] tabular-nums text-[#101828]">{fmt(ev.total)} {ev.currency}</b><span className="block">excl. VAT · {ev.creators} creators{ev.impressions ? ` · ${short(ev.impressions)} impressions` : ''}</span></> : <span>{p.brand.name} · {STEPS[step]}</span>}
        </div>
        {step > 0 && <button type="button" onClick={() => goTo((step - 1) as Step)} className="h-11 rounded-xl border border-gray-200 bg-white px-5 text-[13px] text-[#344054] shadow-sm hover:bg-gray-50" style={{ fontWeight: 600 }}>Back</button>}
        {step < 8 && (
          <button type="button" disabled={!canLeave(step)} onClick={() => goTo((step + 1) as Step)} className="inline-flex h-11 items-center gap-2 rounded-xl bg-[#be43ff] px-6 text-sm text-white transition-all hover:bg-[#a830e0] active:scale-[0.97] disabled:opacity-40" style={{ fontWeight: 600 }}>
            Next: {STEPS[step + 1].toLowerCase()}<ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        )}
      </footer>
      </div>
    </div>
  );
}
